import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import * as subs from '../graphql/subscriptions';
import useOperationalStore, {
  STATUS_TO_STAGE,
  ACTIVITY_TYPES,
  BLOCKER_TYPES,
  BLOCKER_LABELS,
  normalizeActivities,
  getStageLabel,
  fmtDur,
} from '../platform/store/useOperationalStore';
import { getCurrentRole, hasPermission, PERMISSIONS, ROLES } from '../shared/permissions';
import { EVENT_TYPES, recordTruckEvent, nowSeconds } from '../platform/events/operationEvents';
import useShopSettings from '../platform/settings/useShopSettings';
import AssignTechModal from '../components/Modals/AssignTechModal';
import CategoryModal from '../components/Modals/CategoryModal';
import rekonLogo from '../Assets/Just Rekon.png';
import { STAGE_ACCENT, PRIMARY_ACTION, SECONDARY_ACTIONS } from './ro/roConstants';
import { tsToMs, fmtTs, clean, makeActivityId } from './ro/roHelpers';
import { getHeaderMoneyNumbers, getNextMoneyAction, getMoneyGateWarning, hasGateOverride, estimateLaborHours } from './ro/roMoney';
import QRCodeImg from './ro/QRCodeImg';
import PhotoViewer from './ro/PhotoViewer';
import DetailSection from './ro/DetailSection';
import PartsSection from './ro/PartsSection';
import RoHeader from './ro/RoHeader';
import BlockerBanner from './ro/BlockerBanner';
import RoTabs from './ro/RoTabs';
import OperationsTable from './ro/OperationsTable';
import ActivityStream from './ro/ActivityStream';
import ActivityForm from './ro/ActivityForm';
import LeftColumnTabs from './ro/LeftColumnTabs';
import PhotosGrid, { collectPhotoKeys } from './ro/PhotosGrid';
import { UnitPanel, CustomerPanel } from './ro/UnitCustomerPanels';
import BlockerPanel from './ro/BlockerPanel';
import MoneyRail from './ro/MoneyRail';
import VoiceEstimateModal from './ro/VoiceEstimateModal';
import TimeCard from './ro/TimeCard';
import { toLaborLines, toPartLines, toFindingText } from '../platform/engine/aiEstimateDraft';
import { mergeLinesIntoEstimate } from '../platform/engine/invoiceDraft';
import { markEntriesBilled } from '../platform/store/timeClockGql';
import { fetchCustomer } from '../platform/engine/identitySpine';
import './ROPage.css';

const client = generateClient();

const getTruckWithCommercial = /* GraphQL */ `
  query GetTruckWithCommercial($id: ID!) {
    getTruck(id: $id) {
      id
      locationId
      repairOrder
      companyName
      truckNumber
      reason
      status
      position
      techNumbers
      warranty
      contactPerson
      phone
      email
      partsHandling
      customerCheckInId
      year
      make
      model
      vin
      mileage
      hours
      faults
      startTime
      lastStatusChange
      lastNote
      timeInWip
      wipStartTime
      isPaused
      notes
      statusHistory { status timeEntered }
      serviceSummary
      repairCategories { category subcategory estimatedTime completionTime }
      repairCategory
      repairSubcategory
      estimatedTime
      completionTime
      expired
      warning
      critical
      completionType
      bayId
      location
      priorityScore
      slaRisk
      downtimeSeverity
      isHardDown
      fleetId
      fleetPriority
      flaggedHours
      partsStatus
      partsEta
      estimatedCompletion
      promisedTime
      blockers { type description since resolvedAt }
      activities {
        id
        type
        text
        author
        timestamp
        photos
        customerFacing
        category
        stage
      }
      currentBlocker { type description setBy setAt resolvedAt resolvedBy owner }
      blockerHistory { type description setBy setAt resolvedAt resolvedBy owner }
      laborType
      customerType
      poNumber
      shopId
      customerId
      unitId
      parts {
        id
        description
        partNumber
        vendor
        quantity
        cost
        price
        status
        orderedAt
        receivedAt
        eta
        notes
        addedBy
        addedAt
      }
      estimates {
        id
        truckId
        roNumber
        status
        lines {
          title
          complaint
          cause
          correction
          taxExempt
          laborLines {
            id
            description
            catalogItemId
            hours
            rate
            flatPrice
            amount
            techName
            notes
          }
          partLineIds
        }
        shopSupplies
        shopSuppliesPct
        taxRate
        subtotal
        tax
        total
        notes
        customerNotes
        token
        sentAt
        approvedAt
        approvalMethod
        approvedBy
        approvalNotes
        declinedAt
        declineReason
        invoicedAt
        lockedAt
        paymentTermsDays
        payments {
          id
          amount
          method
          reference
          note
          receivedAt
          recordedBy
        }
        createdBy
        createdAt
        updatedAt
        version
      }
      createdAt
      updatedAt
    }
  }
`;

// ─── Main ROPage component ────────────────────────────────────────────────────

export default function ROPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const localUpdateTruck = useOperationalStore(s => s.localUpdateTruck);
  const technicians      = useOperationalStore(s => s.technicians);

  const [truck, setTruck]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [now, setNow]             = useState(() => Date.now());
  const [activeForm, setActiveForm]       = useState(null); // 'note'|'finding'|'repair'|'photo'
  const [activeTab, setActiveTab]         = useState('activity'); // 'activity'|'parts'|'time'
  const [leftTab, setLeftTab]             = useState('activity'); // 'activity'|'findings'|'photos'|'unit'|'customer'
  const [submitting, setSubmitting]       = useState(false);
  const [customerTermsDays, setCustomerTermsDays] = useState(null);
  const [customerPoRequired, setCustomerPoRequired] = useState(false);
  const [confirmVoid, setConfirmVoid]     = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [advancingStage, setAdvancingStage]   = useState(false);
  const [editingPromise, setEditingPromise]   = useState(false);
  const [promiseInput, setPromiseInput]       = useState('');
  const [assignTechOpen, setAssignTechOpen]   = useState(false);
  const [categoryOpen, setCategoryOpen]       = useState(false);
  const [photoViewer, setPhotoViewer]         = useState(null);
  const [voiceOpen, setVoiceOpen]             = useState(false);
  const [blockerFormOpen, setBlockerFormOpen] = useState(false);
  const [blockerType, setBlockerType]         = useState(BLOCKER_TYPES.WAITING_PARTS);
  const [blockerDesc, setBlockerDesc]         = useState('');
  const [blockerOwner, setBlockerOwner]       = useState('');
  const [partsBlockerPrompt,      setPartsBlockerPrompt]      = useState(false);
  const [partsClearBlockerPrompt, setPartsClearBlockerPrompt] = useState(false);
  // Which money-rail card is expanded: null | 'estimate' | 'labor' | 'parts' | 'invoice' | 'comms'
  const [expandedMoneyCard, setExpandedMoneyCard] = useState(null);
  // Which surface the estimate overlay opens to: 'builder' (Estimate) or
  // 'preview' (Invoice). The header's Invoice button opens the invoice; the
  // Estimate card/tab open the builder.
  const [estimateMode, setEstimateMode] = useState('builder');
  const openMoneyCard = (card, mode = 'builder') => {
    if (card === 'estimate') setEstimateMode(mode);
    setExpandedMoneyCard(card);
  };
  const openEstimate = () => openMoneyCard('estimate', 'builder');
  const openInvoice  = () => openMoneyCard('estimate', 'preview');

  const partsMarkup = useShopSettings(s => s.settings?.pricing?.partsMarkup ?? 0);
  const shopSettings = useShopSettings(s => s.settings);

  const currentRole = getCurrentRole();
  const canMove         = hasPermission(currentRole, PERMISSIONS.MOVE_WORKFLOW_STAGE);
  const canNote         = hasPermission(currentRole, PERMISSIONS.ADD_NOTES);
  const canEdit         = hasPermission(currentRole, PERMISSIONS.EDIT_WORK_ORDERS);
  const canClose        = hasPermission(currentRole, PERMISSIONS.CLOSE_WORK_ORDERS);
  const canVoid         = hasPermission(currentRole, PERMISSIONS.VOID_WORK_ORDERS);
  const canPhoto        = hasPermission(currentRole, PERMISSIONS.ADD_PHOTOS);
  const canManageParts  = canEdit;
  const canViewCost     = [ROLES.ADMIN, ROLES.MANAGER, ROLES.SERVICE_WRITER, ROLES.ACCOUNTING].includes(currentRole);

  // ── Live clock (every minute) ──────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // ── Fetch truck ─────────────────────────────────────────────────────────────
  const loadTruck = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.graphql({ query: getTruckWithCommercial, variables: { id } });
      const data = res.data?.getTruck;
      if (!data) { setError('RO not found.'); setLoading(false); return; }
      setTruck(data);
    } catch (err) {
      setError('Failed to load RO. Check your connection.');
      console.error('[ROPage] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTruck(); }, [loadTruck]);

  // ── Customer terms ──────────────────────────────────────────────────────────
  // Net terms belong to the customer, not the shop. Fetched once per job so a
  // new estimate stamps THEIR terms; jobs with no linked customer (walk-ins,
  // pre-backfill records) fall back to the shop default inside EstimatePanel.
  useEffect(() => {
    const customerId = truck?.customerId;
    if (!customerId) { setCustomerTermsDays(null); setCustomerPoRequired(false); return; }
    let cancelled = false;
    fetchCustomer(customerId).then(c => {
      if (cancelled) return;
      setCustomerTermsDays(c?.paymentTermsDays ?? null);
      setCustomerPoRequired(Boolean(c?.poRequired));
    });
    return () => { cancelled = true; };
  }, [truck?.customerId]);

  // ── Live subscription ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const sub = client.graphql({
      query: subs.onUpdateTruck,
      variables: { filter: { id: { eq: id } } },
    }).subscribe({
      next: ({ data }) => {
        const updated = data?.onUpdateTruck;
        if (updated) {
          setTruck(current => ({
            ...(current || {}),
            ...updated,
            estimates: updated.estimates || current?.estimates || [],
          }));
        }
      },
      error: (err) => console.warn('[ROPage] subscription error:', err),
    });
    return () => sub.unsubscribe?.();
  }, [id]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function advanceStage(nextStatus) {
    if (!canMove || !truck || advancingStage) return;

    // Soft money gate: skipping a money step (e.g. starting repair with no
    // approved estimate) asks once and logs the override — never hard-blocks.
    const gate = getMoneyGateWarning(truck, nextStatus, shopSettings);
    let gateActivity = null;
    if (gate && !hasGateOverride(truck, gate.id)) {
      if (!window.confirm(gate.prompt)) return;
      gateActivity = {
        id: makeActivityId(),
        type: ACTIVITY_TYPES.AUTHORIZATION,
        text: `${gate.overrideText} — by ${currentRole}`,
        author: currentRole,
        timestamp: nowSeconds(),
        photos: [],
        customerFacing: false,
        category: null,
        stage: STATUS_TO_STAGE[nextStatus] || null,
      };
    }

    setAdvancingStage(true);
    const ts = nowSeconds();
    const cleanHistory = (truck.statusHistory || [])
      .filter(e => e?.status && e?.timeEntered)
      .map(e => ({ status: e.status, timeEntered: e.timeEntered }));
    const newHistory = [...cleanHistory, { status: nextStatus, timeEntered: ts }];

    const stageActivity = {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.STATUS_CHANGE,
      text: `${getStageLabel(truck.status)} → ${getStageLabel(nextStatus)}`,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: STATUS_TO_STAGE[nextStatus] || null,
    };
    const updatedActivities = [
      stageActivity,
      ...(gateActivity ? [gateActivity] : []),
      ...clean(truck.activities || []),
    ];

    const patch = {
      status: nextStatus,
      lastStatusChange: ts,
      statusHistory: newHistory,
      activities: updatedActivities,
    };

    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);

    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, ...patch } },
      });
      recordTruckEvent(truck, EVENT_TYPES.STATUS_CHANGED, {
        timestamp: ts,
        previousStatus: truck.status,
        newStatus: nextStatus,
      });
    } catch (err) { console.error('[ROPage] advanceStage error:', err); }
    setAdvancingStage(false);
  }

  async function handleAddActivity({ type, text, category, customerFacing, photos }) {
    if (!canNote || !truck) return;
    setSubmitting(true);
    const ts = nowSeconds();
    const newEntry = {
      id: makeActivityId(),
      type,
      text,
      author: currentRole,
      timestamp: ts,
      photos,
      customerFacing,
      category,
      stage: STATUS_TO_STAGE[truck.status] || null,
    };
    const updatedActivities = [newEntry, ...clean(truck.activities || [])];
    const patch = { activities: updatedActivities, lastNote: ts };
    setTruck(t => ({ ...t, ...patch }));
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, ...patch } },
      });
      if (photos?.length) {
        recordTruckEvent(truck, EVENT_TYPES.PHOTO_ADDED, { timestamp: ts });
      } else {
        recordTruckEvent(truck, EVENT_TYPES.NOTE_ADDED, { timestamp: ts, note: text });
      }
    } catch (err) { console.error('[ROPage] addActivity error:', err); }
    setSubmitting(false);
  }

  async function handleSetBlocker() {
    if (!canEdit || !truck) return;
    const ts = nowSeconds();
    const newBlocker = clean({
      type: blockerType,
      description: blockerDesc || null,
      setBy: currentRole,
      setAt: ts,
      resolvedAt: null,
      resolvedBy: null,
      owner: blockerOwner || null,
    });
    const blockerActivity = {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.BLOCKER_SET,
      text: `${BLOCKER_LABELS[blockerType]}${blockerDesc ? `: ${blockerDesc}` : ''}`,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: STATUS_TO_STAGE[truck.status] || null,
    };
    const updatedActivities = [blockerActivity, ...clean(truck.activities || [])];
    const prevHistory = clean(truck.blockerHistory || []);
    const patch = {
      currentBlocker: newBlocker,
      blockerHistory: [...prevHistory, newBlocker],
      activities: updatedActivities,
    };
    setTruck(t => ({ ...t, ...patch }));
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] setBlocker error:', err); }
    setBlockerFormOpen(false);
    setBlockerDesc('');
    setBlockerOwner('');
  }

  async function handleClearBlocker() {
    if (!canEdit || !truck?.currentBlocker) return;
    const ts = nowSeconds();
    const clearActivity = {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.BLOCKER_CLEARED,
      text: `Unblocked by ${currentRole}`,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: false,
      category: null,
      stage: STATUS_TO_STAGE[truck.status] || null,
    };
    const resolvedBlocker = clean({ ...truck.currentBlocker, resolvedAt: ts, resolvedBy: currentRole });
    const prevHistory = clean(truck.blockerHistory || []);
    const updatedHistory = prevHistory.length
      ? [...prevHistory.slice(0, -1), resolvedBlocker]
      : [resolvedBlocker];
    const updatedActivities = [clearActivity, ...clean(truck.activities || [])];
    const patch = { currentBlocker: null, blockerHistory: updatedHistory, activities: updatedActivities };
    setTruck(t => ({ ...t, ...patch }));
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] clearBlocker error:', err); }
  }

  async function handleComplete() {
    if (!canClose || !truck) return;

    // Soft money gate: closing without an invoice asks once and logs the
    // override — the job stays visible in Money's Unbilled queue either way.
    const gate = getMoneyGateWarning(truck, 'completed', shopSettings);
    let gateActivity = null;
    if (gate && !hasGateOverride(truck, gate.id)) {
      if (!window.confirm(gate.prompt)) { setConfirmComplete(false); return; }
      gateActivity = {
        id: makeActivityId(),
        type: ACTIVITY_TYPES.AUTHORIZATION,
        text: `${gate.overrideText} — by ${currentRole}`,
        author: currentRole,
        timestamp: nowSeconds(),
        photos: [],
        customerFacing: false,
        category: null,
        stage: STATUS_TO_STAGE[truck.status] || null,
      };
    }

    const ts = nowSeconds();
    const cleanHistory = (truck.statusHistory || [])
      .filter(e => e?.status && e?.timeEntered)
      .map(e => ({ status: e.status, timeEntered: e.timeEntered }));
    const newHistory = [...cleanHistory, { status: 'completed', timeEntered: ts }];
    const patch = {
      status: 'completed', completionTime: ts, lastStatusChange: ts, statusHistory: newHistory,
      ...(gateActivity ? { activities: [gateActivity, ...clean(truck.activities || [])] } : {}),
    };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
      recordTruckEvent(truck, EVENT_TYPES.COMPLETED, { timestamp: ts, previousStatus: truck.status, newStatus: 'completed' });
    } catch (err) { console.error('[ROPage] complete error:', err); }
    setConfirmComplete(false);
  }

  async function handleVoid() {
    if (!canVoid || !truck) return;
    const ts = nowSeconds();
    const cleanHistory = (truck.statusHistory || [])
      .filter(e => e?.status && e?.timeEntered)
      .map(e => ({ status: e.status, timeEntered: e.timeEntered }));
    const newHistory = [...cleanHistory, { status: 'voided', timeEntered: ts }];
    const patch = { status: 'voided', completionTime: ts, lastStatusChange: ts, statusHistory: newHistory };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
      recordTruckEvent(truck, EVENT_TYPES.VOIDED, { timestamp: ts, previousStatus: truck.status, newStatus: 'voided' });
    } catch (err) { console.error('[ROPage] void error:', err); }
    setConfirmVoid(false);
  }

  async function handleSavePromise() {
    if (!promiseInput || !truck) return;
    const ms = new Date(promiseInput).getTime();
    if (isNaN(ms)) return;
    const secs = Math.floor(ms / 1000);
    setTruck(t => ({ ...t, promisedTime: secs }));
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, promisedTime: secs } } });
    } catch (err) { console.error('[ROPage] savePromise error:', err); }
    setEditingPromise(false);
  }

  async function handleClearPromise() {
    if (!truck) return;
    setTruck(t => ({ ...t, promisedTime: null }));
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, promisedTime: null } } });
    } catch (err) { console.error('[ROPage] clearPromise error:', err); }
    setEditingPromise(false);
  }

  async function handleUpdateTechs(techNumbers) {
    if (!canEdit || !truck) return;
    setTruck(t => ({ ...t, techNumbers }));
    localUpdateTruck(truck.id, { techNumbers });
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, techNumbers } } });
      recordTruckEvent(truck, EVENT_TYPES.ASSIGNED, { note: `Techs: ${techNumbers.join(', ') || 'none'}` });
    } catch (err) { console.error('[ROPage] updateTechs error:', err); }
    setAssignTechOpen(false);
  }

  async function handleCategoryUpdate(categories) {
    if (!canEdit || !truck) return;
    setTruck(t => ({ ...t, repairCategories: categories }));
    localUpdateTruck(truck.id, { repairCategories: categories });
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, repairCategories: clean(categories) } } });
    } catch (err) { console.error('[ROPage] categoryUpdate error:', err); }
    setCategoryOpen(false);
  }

  // ── Parts handlers ──────────────────────────────────────────────────────────

  async function handleAddPart(formData) {
    if (!canManageParts || !truck) return;
    const ts = nowSeconds();
    const partId = `pl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const newPart = clean({
      id: partId,
      description: formData.description.trim(),
      partNumber:  formData.partNumber?.trim() || null,
      vendor:      formData.vendor?.trim() || null,
      quantity:    parseFloat(formData.quantity) || 1,
      cost:        parseFloat(formData.cost) || 0,
      price:       parseFloat(formData.price) || 0,
      status:      formData.status || 'ordered',
      orderedAt:   formData.status === 'ordered' ? ts : null,
      receivedAt:  formData.status === 'received' ? ts : null,
      eta:         formData.eta ? Math.floor(new Date(formData.eta).getTime() / 1000) : null,
      notes:       null,
      addedBy:     currentRole,
      addedAt:     ts,
    });
    const actEntry = {
      id: makeActivityId(), type: ACTIVITY_TYPES.NOTE,
      text: `Part added: ${newPart.description} — ${newPart.status}`,
      author: currentRole, timestamp: ts,
      photos: [], customerFacing: false, category: null,
      stage: STATUS_TO_STAGE[truck.status] || null,
    };
    const updatedParts      = [...clean(truck.parts || []), newPart];
    const updatedActivities = [actEntry, ...clean(truck.activities || [])];
    const patch = { parts: updatedParts, activities: updatedActivities, lastNote: ts };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] addPart error:', err); }
    if (['ordered', 'pending'].includes(newPart.status) && !truck.currentBlocker) {
      setPartsBlockerPrompt(true);
    }
  }

  async function handleUpdatePart(partId, changes) {
    if (!canManageParts || !truck) return;
    const updatedParts = clean(truck.parts || []).map(p => {
      if (p.id !== partId) return p;
      return {
        ...p,
        description: changes.description?.trim() || p.description,
        partNumber:  changes.partNumber?.trim()  || null,
        vendor:      changes.vendor?.trim()       || null,
        quantity:    parseFloat(changes.quantity) || p.quantity,
        cost:        parseFloat(changes.cost)  ?? p.cost,
        price:       parseFloat(changes.price) ?? p.price,
        status:      changes.status || p.status,
        eta:         changes.eta ? Math.floor(new Date(changes.eta).getTime() / 1000) : (p.eta ?? null),
      };
    });
    const patch = { parts: updatedParts };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] updatePart error:', err); }
  }

  async function handleDeletePart(partId) {
    if (!canManageParts || !truck) return;
    const updatedParts = clean(truck.parts || []).filter(p => p.id !== partId);
    const patch = { parts: updatedParts };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] deletePart error:', err); }
  }

  async function handlePartStatusChange(partId, newStatus) {
    if (!canManageParts || !truck) return;
    const ts = nowSeconds();
    const part = (truck.parts || []).find(p => p.id === partId);
    if (!part) return;
    const updatedParts = clean(truck.parts || []).map(p => {
      if (p.id !== partId) return p;
      return {
        ...p,
        status:     newStatus,
        receivedAt: newStatus === 'received' ? ts : (p.receivedAt ?? null),
        orderedAt:  newStatus === 'ordered' && !p.orderedAt ? ts : (p.orderedAt ?? null),
      };
    });
    const activities = [...clean(truck.activities || [])];
    if (newStatus === 'received') {
      activities.unshift({
        id: makeActivityId(), type: ACTIVITY_TYPES.NOTE,
        text: `Part received: ${part.description}`,
        author: currentRole, timestamp: ts,
        photos: [], customerFacing: false, category: null,
        stage: STATUS_TO_STAGE[truck.status] || null,
      });
      const allReceived = updatedParts.length > 0 && updatedParts.every(p => p.status === 'received');
      if (allReceived) {
        activities.unshift({
          id: makeActivityId(), type: ACTIVITY_TYPES.NOTE,
          text: 'All parts received — ready to begin repair',
          author: currentRole, timestamp: ts + 1,
          photos: [], customerFacing: false, category: null,
          stage: STATUS_TO_STAGE[truck.status] || null,
        });
        if (truck.currentBlocker?.type === 'waiting_parts') {
          setPartsClearBlockerPrompt(true);
        }
      }
    }
    const patch = { parts: updatedParts, activities, lastNote: ts };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] partStatusChange error:', err); }
  }

  async function handleSetPartsBlocker() {
    if (!canEdit || !truck) return;
    const ts = nowSeconds();
    const newBlocker = clean({
      type: BLOCKER_TYPES.WAITING_PARTS, description: null,
      setBy: currentRole, setAt: ts, resolvedAt: null, resolvedBy: null, owner: null,
    });
    const blockerActivity = {
      id: makeActivityId(), type: ACTIVITY_TYPES.BLOCKER_SET,
      text: BLOCKER_LABELS[BLOCKER_TYPES.WAITING_PARTS],
      author: currentRole, timestamp: ts,
      photos: [], customerFacing: false, category: null,
      stage: STATUS_TO_STAGE[truck.status] || null,
    };
    const updatedActivities = [blockerActivity, ...clean(truck.activities || [])];
    const prevHistory = clean(truck.blockerHistory || []);
    const patch = {
      currentBlocker: newBlocker,
      blockerHistory: [...prevHistory, newBlocker],
      activities: updatedActivities,
    };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] setPartsBlocker error:', err); }
    setPartsBlockerPrompt(false);
  }

  async function handleSaveEstimate(estimateDraft, options = {}) {
    if (!truck) return;
    const existing = clean(truck.estimates || []);
    const idx = existing.findIndex(e => e.id === estimateDraft.id);
    const updated = idx >= 0
      ? existing.map((e, i) => i === idx ? estimateDraft : e)
      : [estimateDraft, ...existing];
    const extraActivities = clean(options.activities || []);
    const patch = {
      estimates: updated,
      ...(extraActivities.length ? {
        activities: [...extraActivities, ...clean(truck.activities || [])],
        lastNote: nowSeconds(),
      } : {}),
    };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({ query: mutations.updateTruck, variables: { input: { id: truck.id, ...patch } } });
    } catch (err) { console.error('[ROPage] saveEstimate error:', err); }
  }

  // Voice draft → a finding, parts, and labor on the working estimate, in one
  // write. The modal has already been through a human review step; this only
  // persists what the writer approved there.
  async function handleApplyVoiceDraft(draft) {
    if (!truck) return;
    const ts = nowSeconds();

    const newParts   = toPartLines(draft, { addedBy: currentRole, timestamp: ts });
    const laborLines = toLaborLines(draft, shopSettings?.pricing?.defaultLaborRate);

    const findingText = toFindingText(draft);
    const findingEntry = findingText ? {
      id: makeActivityId(),
      type: ACTIVITY_TYPES.FINDING,
      text: findingText,
      author: currentRole,
      timestamp: ts,
      photos: [],
      customerFacing: Boolean(draft.customerExplanation),
      category: draft.category || null,
      stage: STATUS_TO_STAGE[truck.status] || null,
    } : null;

    const estimate = mergeLinesIntoEstimate(truck, shopSettings, {
      laborLines,
      newParts,
      complaint:  draft.complaint,
      cause:      draft.cause,
      correction: draft.correction,
      termsDays:  customerTermsDays,
    });

    const existingEstimates = clean(truck.estimates || []);
    const idx = existingEstimates.findIndex(e => e.id === estimate.id);
    const updatedEstimates = idx >= 0
      ? existingEstimates.map((e, i) => i === idx ? estimate : e)
      : [estimate, ...existingEstimates];

    const patch = {
      estimates: updatedEstimates,
      ...(newParts.length ? { parts: [...clean(truck.parts || []), ...newParts] } : {}),
      ...(findingEntry ? {
        activities: [findingEntry, ...clean(truck.activities || [])],
        lastNote: ts,
      } : {}),
    };

    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    await client.graphql({
      query: mutations.updateTruck,
      variables: { input: { id: truck.id, ...patch } },
    });
    if (findingEntry) {
      recordTruckEvent(truck, EVENT_TYPES.NOTE_ADDED, { timestamp: ts, note: findingText });
    }
  }

  // Clocked hours → labor lines on the working estimate. Same merge path as the
  // voice draft, so the money still prices through calcTotals. The entries are
  // stamped billed only after the estimate save lands — a failed save must not
  // leave hours marked billed that nobody can find on an invoice.
  async function handleBillClockedHours(laborLines, entries) {
    if (!truck || !laborLines.length) return;
    const estimate = mergeLinesIntoEstimate(truck, shopSettings, { laborLines, termsDays: customerTermsDays });

    const existingEstimates = clean(truck.estimates || []);
    const idx = existingEstimates.findIndex(e => e.id === estimate.id);
    const updatedEstimates = idx >= 0
      ? existingEstimates.map((e, i) => i === idx ? estimate : e)
      : [estimate, ...existingEstimates];

    const patch = { estimates: updatedEstimates };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, ...patch } },
      });
    } catch (err) {
      console.error('[ROPage] bill clocked hours error:', err);
      return;
    }
    await markEntriesBilled(entries, estimate.id);
  }

  async function handleSavePO(poNumber) {
    if (!truck) return;
    const patch = { poNumber };
    setTruck(t => ({ ...t, ...patch }));
    localUpdateTruck(truck.id, patch);
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: { input: { id: truck.id, ...patch } },
      });
    } catch (err) { console.error('[ROPage] save PO error:', err); }
  }

  function handlePrint() { window.print(); }

  function handleShare() {
    const url = window.location.href;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
    } else {
      prompt('Copy this link:', url);
    }
  }

  function handleBack() {
    // Back peels one layer: an expanded money-rail card collapses first.
    if (expandedMoneyCard) { setExpandedMoneyCard(null); return; }
    // Then return to wherever the user came from (Priority, Collections,
    // Dashboard…); '/flow' only on a direct link.
    if (routerLocation.key !== 'default') navigate(-1);
    else navigate('/flow');
  }

  // ── Derived state ────────────────────────────────────────────────────────────

  if (loading) return <div className="ro-loading">Loading RO…</div>;
  if (error)   return <div className="ro-error">{error} <button onClick={loadTruck}>Retry</button></div>;
  if (!truck)  return <div className="ro-error">RO not found.</div>;

  const isClosed  = ['completed', 'voided'].includes(truck.status);
  const stageId   = STATUS_TO_STAGE[truck.status] || 'intake';
  const stageAccent = STAGE_ACCENT[stageId] || '#64748b';
  const stageMs   = truck.lastStatusChange ? now - tsToMs(truck.lastStatusChange) : 0;
  const totalMs   = truck.startTime ? now - tsToMs(truck.startTime) : 0;
  const activities = normalizeActivities(truck);
  const findingsActivities = activities.filter(a => a.type === ACTIVITY_TYPES.FINDING);
  const photoCount = collectPhotoKeys(activities).length;
  const lastActivity = activities[0] || null;
  const techs = truck.techNumbers?.filter(Boolean) || [];
  const primaryAction = !isClosed ? PRIMARY_ACTION[truck.status] : null;
  const secondaryActions = !isClosed ? (SECONDARY_ACTIONS[truck.status] || []) : [];
  const hasPromise = !!truck.promisedTime;
  const promiseMs = hasPromise ? tsToMs(truck.promisedTime) : null;
  const promiseOverdue = hasPromise && promiseMs < now;
  const promiseRemaining = hasPromise ? (promiseMs - now) : null;
  const currentBlocker = truck.currentBlocker ?? null;
  const pageUrl = `${window.location.origin}/ro/${truck.id}`;
  const opLines = truck.estimates?.[0]?.lines || [];
  const { approvedAmount, unbilledHours } = getHeaderMoneyNumbers(truck);
  const moneyAction = !isClosed ? getNextMoneyAction(truck, shopSettings) : null;

  const partsProps = {
    truck,
    canManageParts,
    canViewCost,
    isClosed,
    partsMarkup,
    onAddPart: handleAddPart,
    onUpdatePart: handleUpdatePart,
    onDeletePart: handleDeletePart,
    onPartStatusChange: handlePartStatusChange,
    blockerPrompt: partsBlockerPrompt,
    onSetBlocker: handleSetPartsBlocker,
    onDismissBlockerPrompt: () => setPartsBlockerPrompt(false),
    clearBlockerPrompt: partsClearBlockerPrompt,
    onClearBlocker: () => { handleClearBlocker(); setPartsClearBlockerPrompt(false); },
    onDismissClearBlockerPrompt: () => setPartsClearBlockerPrompt(false),
  };

  return (
    <div className="ro-page">

      <RoHeader
        truck={truck} stageAccent={stageAccent} currentBlocker={currentBlocker} isClosed={isClosed}
        stageMs={stageMs} totalMs={totalMs} now={now}
        hasPromise={hasPromise} promiseMs={promiseMs} promiseOverdue={promiseOverdue} promiseRemaining={promiseRemaining}
        primaryAction={primaryAction} secondaryActions={secondaryActions} advancingStage={advancingStage}
        canMove={canMove} canClose={canClose} canVoid={canVoid}
        confirmComplete={confirmComplete} setConfirmComplete={setConfirmComplete}
        confirmVoid={confirmVoid} setConfirmVoid={setConfirmVoid} currentRole={currentRole}
        onOpenEstimate={openEstimate} onOpenInvoice={openInvoice} onBack={handleBack}
        advanceStage={advanceStage} handleComplete={handleComplete} handleVoid={handleVoid}
        handlePrint={handlePrint} handleShare={handleShare}
        editingPromise={editingPromise} setEditingPromise={setEditingPromise}
        promiseInput={promiseInput} setPromiseInput={setPromiseInput}
        handleSavePromise={handleSavePromise} onClearPromise={handleClearPromise}
        techs={techs} setAssignTechOpen={setAssignTechOpen} canEdit={canEdit} lastActivity={lastActivity}
        approvedAmount={approvedAmount} unbilledHours={unbilledHours} moneyAction={moneyAction} settings={shopSettings}
      />

      <BlockerBanner
        truck={truck} currentBlocker={currentBlocker} now={now}
        onOpenEstimate={openEstimate}
        onViewParts={() => setActiveTab('parts')}
      />

      <RoTabs
        activeTab={activeTab} setActiveTab={setActiveTab}
        partsCount={truck.parts?.length} estimatesCount={truck.estimates?.length}
        onOpenEstimate={openEstimate}
      />

      {activeTab === 'activity' && opLines.length > 0 && (
        <OperationsTable lines={opLines} parts={truck.parts} onOpen={openEstimate} />
      )}

      {/* ── MAIN BODY ─────────────────────────────────────────────────────── */}
      <div className="ro-body" style={activeTab !== 'activity' ? { display: 'none' } : undefined}>

        <div className="ro-stream-col">
          <LeftColumnTabs
            activeTab={leftTab} setActiveTab={setLeftTab}
            findingsCount={findingsActivities.length} photosCount={photoCount}
          />

          {!isClosed && canNote && ['activity', 'findings'].includes(leftTab) && (
            <button type="button" className="ro-voice-btn" onClick={() => setVoiceOpen(true)}>
              🎤 Talk it out — turn a spoken finding into estimate lines
            </button>
          )}

          {leftTab === 'activity' && (
            <ActivityStream
              isClosed={isClosed} canNote={canNote} canPhoto={canPhoto}
              activeForm={activeForm} setActiveForm={setActiveForm} submitting={submitting}
              onSubmitActivity={handleAddActivity} activities={activities} onPhotoExpand={setPhotoViewer}
            />
          )}

          {leftTab === 'findings' && (
            <ActivityStream
              isClosed={isClosed} canNote={canNote} canPhoto={false}
              activeForm={activeForm} setActiveForm={setActiveForm} submitting={submitting}
              onSubmitActivity={handleAddActivity} activities={findingsActivities} onPhotoExpand={setPhotoViewer}
              toolbarTypes={[{ type: ACTIVITY_TYPES.FINDING, label: '+ Finding', show: canNote }]}
              emptyMessage="No findings recorded yet."
            />
          )}

          {leftTab === 'photos' && (
            <>
              {!isClosed && canPhoto && (
                <div className="ro-act-toolbar">
                  <button
                    className={`ro-act-type-btn ${activeForm === ACTIVITY_TYPES.PHOTO ? 'active' : ''}`}
                    onClick={() => setActiveForm(f => f === ACTIVITY_TYPES.PHOTO ? null : ACTIVITY_TYPES.PHOTO)}>
                    + Photo
                  </button>
                </div>
              )}
              {activeForm === ACTIVITY_TYPES.PHOTO && (
                <ActivityForm
                  activeType={ACTIVITY_TYPES.PHOTO}
                  onClose={() => setActiveForm(null)}
                  onSubmit={handleAddActivity}
                  disabled={submitting}
                />
              )}
              <PhotosGrid activities={activities} onPhotoExpand={setPhotoViewer} />
            </>
          )}

          {leftTab === 'unit'     && <UnitPanel truck={truck} />}
          {leftTab === 'customer' && (
            <CustomerPanel
              truck={truck}
              canEdit={canEdit && !isClosed}
              poRequired={customerPoRequired}
              onSavePO={handleSavePO}
            />
          )}
        </div>

        {/* ── RIGHT: Money rail + details ──────────────────────────────────── */}
        <aside className="ro-details-col">

          <MoneyRail
            truck={truck} settings={shopSettings} canEdit={canEdit}
            customerTermsDays={customerTermsDays}
            poRequired={customerPoRequired}
            onSaveEstimate={handleSaveEstimate} now={now}
            onOpenPartsTab={() => setActiveTab('parts')}
            expanded={expandedMoneyCard} onExpand={openMoneyCard} estimateMode={estimateMode}
          />

          <BlockerPanel
            currentBlocker={currentBlocker} canEdit={canEdit} isClosed={isClosed} now={now}
            blockerFormOpen={blockerFormOpen} setBlockerFormOpen={setBlockerFormOpen}
            blockerType={blockerType} setBlockerType={setBlockerType}
            blockerDesc={blockerDesc} setBlockerDesc={setBlockerDesc}
            blockerOwner={blockerOwner} setBlockerOwner={setBlockerOwner}
            handleSetBlocker={handleSetBlocker} handleClearBlocker={handleClearBlocker}
          />

          {/* Repair Categories */}
          <DetailSection title="Repair Categories" defaultOpen={false}>
            {(truck.repairCategories || []).length === 0
              ? <span className="ro-muted">None assigned</span>
              : (truck.repairCategories || []).map((c, i) => (
                  <div key={i} className="ro-cat-row">
                    <span>{c.category}{c.subcategory ? ` — ${c.subcategory}` : ''}</span>
                    {c.estimatedTime && <span className="ro-cat-time">{c.estimatedTime}h est</span>}
                  </div>
                ))
            }
            {canEdit && !isClosed && (
              <button className="ro-btn ro-btn--ghost ro-btn--sm" onClick={() => setCategoryOpen(true)}>
                {(truck.repairCategories || []).length ? 'Edit' : '+ Add'}
              </button>
            )}
          </DetailSection>

          {/* Promised Time */}
          <DetailSection title="Promised Time" defaultOpen={false}>
            {hasPromise ? (
              <div>
                <div className={`ro-promise-detail ${promiseOverdue ? 'overdue' : ''}`}>
                  {fmtTs(truck.promisedTime)}
                  {promiseOverdue
                    ? <span className="ro-overdue-tag">OVERDUE {fmtDur(Math.abs(promiseRemaining))}</span>
                    : <span className="ro-remaining-tag">in {fmtDur(promiseRemaining)}</span>}
                </div>
                {canEdit && !isClosed && (
                  <button className="ro-btn ro-btn--ghost ro-btn--sm" onClick={() => {
                    const d = new Date(promiseMs);
                    setPromiseInput(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
                    setEditingPromise(true);
                  }}>Edit</button>
                )}
              </div>
            ) : (
              <>
                <span className="ro-muted">Not set</span>
                {canEdit && !isClosed && (
                  <button className="ro-btn ro-btn--ghost ro-btn--sm" onClick={() => {
                    const d = new Date(Date.now() + 4 * 3_600_000);
                    setPromiseInput(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
                    setEditingPromise(true);
                  }}>+ Set</button>
                )}
              </>
            )}
          </DetailSection>

        </aside>
      </div>

      {/* ── PARTS (own tab, full width) ───────────────────────────────────── */}
      <div className="ro-parts-section" style={activeTab !== 'parts' ? { display: 'none' } : undefined}>
        <DetailSection
          title={`Parts${truck.parts?.length ? ` (${truck.parts.length})` : ''}`}
          defaultOpen={!!(truck.parts?.length > 0 || canManageParts)}
        >
          <PartsSection {...partsProps} />
        </DetailSection>
      </div>

      {/* ── TIME (own tab, full width) ─────────────────────────────────────── */}
      {activeTab === 'time' && (
        <div className="ro-parts-section">
          <TimeCard
            truck={truck}
            technicians={technicians}
            invoicedHours={estimateLaborHours(truck)}
            canEdit={canEdit && !isClosed}
            defaultLaborRate={shopSettings?.pricing?.defaultLaborRate}
            onBillHours={handleBillClockedHours}
          />
        </div>
      )}

      {/* ── PRINT HEADER (hidden on screen) ─────────────────────────────── */}
      <div className="ro-print-header">
        <img src={rekonLogo} alt="REKON" className="ro-print-logo" />
        <div>
          <div className="ro-print-ro">RO {truck.repairOrder}</div>
          <div className="ro-print-meta">
            Printed {new Date().toLocaleString()} · {truck.companyName} · {getStageLabel(truck.status)}
          </div>
        </div>
        <QRCodeImg url={pageUrl} size={80} />
      </div>

      {/* ── MODALS ────────────────────────────────────────────────────────── */}
      {assignTechOpen && (
        <AssignTechModal
          onClose={() => setAssignTechOpen(false)}
          onSubmit={handleUpdateTechs}
          currentTechs={techs}
        />
      )}
      {categoryOpen && (
        <CategoryModal
          onClose={() => setCategoryOpen(false)}
          onSubmit={handleCategoryUpdate}
          currentCategories={truck.repairCategories || []}
        />
      )}
      {photoViewer && <PhotoViewer uri={photoViewer} onClose={() => setPhotoViewer(null)} />}

      {voiceOpen && (
        <VoiceEstimateModal
          truck={truck}
          onClose={() => setVoiceOpen(false)}
          onApply={handleApplyVoiceDraft}
        />
      )}
    </div>
  );
}
